/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GroupedTicket, TicketFieldsService } from '@touchplan/ticket';
import { Role, Location, Ticket, ProjectMember, Priority } from '@touchplan/common';
/**
 * TicketEditUtils is intended to be a component service, not a root service.
 * "Utils" is not a great name but this class seems to serve 3 purposes
 * - a bridge that converts and emits docked ticket output to the rest of the app
 * - something of a state service for docked ticket
 * - provides a suite of boilerplate reduction functions for [[TicketFieldsService]]
 */
@Injectable()
export class TicketEditUtils {
	private shutdownSubject$ = new Subject<void>();
	private groupedTicketSubject$: BehaviorSubject<GroupedTicket> = new BehaviorSubject(null);
	/**
	 * Grouped ticket to work with. Initialized via {@link init} function
	 */
	public groupedTicket$ = this.groupedTicketSubject$.asObservable();
	public groupedTicketSync: GroupedTicket = null;

	private changedDataSubject$ = new Subject<object>();
	/**
	 * Output observable that emits whenever there's a change to the GroupedTicket. The output object is
	 * compatible with SplitData's localUpdate (eg. key/values changed).
	 */
	public changedData$ = this.changedDataSubject$.asObservable();

	private wantsToCloseSubject$ = new Subject<void>();
	/**
	 * Observable that emits when the form wants to close.
	 */
	public wantsToClose$ = this.wantsToCloseSubject$.asObservable();

	private persistChangesSubject$ = new Subject<void>();
	/**
	 * Observable that emits when the form wants to save.
	 */
	public persistChanges$ = this.persistChangesSubject$.asObservable();

	private wantsToRevertDataSubject$ = new Subject<void>();
	/**
	 * Observable that emits when the form wants to reset all of it's unsaved changes
	 */
	public wantsToRevertData$ = this.wantsToRevertDataSubject$.asObservable();

	constructor(private ticketFieldsService: TicketFieldsService) {}

	/** Lifecycle init method. This will be treated as an ngOnInit since angular services don't actually execute this function. */
	public init(groupedTicket$: Observable<GroupedTicket>) {
		groupedTicket$.subscribe(this.groupedTicketSubject$);
		this.groupedTicketSubject$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((t) => (this.groupedTicketSync = t));
	}

	/** Lifecycle destroy method. */
	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public updateLiveStart(liveStart: string) {
		this.updateDateRange(liveStart, this.groupedTicketSubject$.value.view.liveFinish);
	}

	public updateLiveFinish(liveFinish: string) {
		this.updateDateRange(this.groupedTicketSubject$.value.view.liveStart, liveFinish);
	}

	public updateDateRange(liveStart: string, liveFinish: string) {
		const data = { liveStart, liveFinish };
		const dataChange = this.ticketFieldsService.setDateRange(this.groupedTicketSubject$.value, data);
		this.emitChanges(dataChange);
	}

	public updateLiveDurationDays(liveDurationDays: number) {
		// Clear the liveExceptionDays for all tickets so that setDuration can recalculate them properly
		// This is usuful when the duration is changed and for example the start date is actually a non-workday
		for (const [_, ticket] of this.groupedTicketSubject$.value.srcList.entries()) {
			ticket.localUpdate({ liveExceptionDays: null });
		}
		const dataChange = this.ticketFieldsService.setDuration(
			this.groupedTicketSubject$.value,
			liveDurationDays
		);
		this.emitChanges(dataChange);
	}

	public updateLivePlanGranularity(livePlanGranularity: string) {
		const changes: any = {
			livePlanGranularity,
		};
		if (livePlanGranularity === 'day') {
			changes.liveDurationHours = null;
		}
		if (livePlanGranularity === 'hour') {
			changes.liveDurationDays = null;
		}
		this.updateFields(changes);
	}

	public updateLiveCrewSize(liveCrewSize: number) {
		this.updateFields({ liveCrewSize });
	}

	public updateWorkdays(workdays: boolean[]) {
		const dataChange = this.ticketFieldsService.setWorkdays(this.groupedTicketSubject$.value, workdays);
		this.emitChanges(dataChange);
	}

	public updateVarianceReason(varianceId: string) {
		const dataChange = this.ticketFieldsService.setCollectedVariance(
			this.groupedTicketSubject$.value,
			varianceId
		);
		this.emitChanges(dataChange);
	}

	public updateVarianceComment(varianceComment: string) {
		const dataChange = this.ticketFieldsService.setCollectedVariance(
			this.groupedTicketSubject$.value,
			undefined,
			varianceComment
		);
		this.emitChanges(dataChange);
	}

	public updateFields(keyValues: object) {
		const dataChange = this.ticketFieldsService.writeDataToGroupedTicket(
			this.groupedTicketSubject$.value,
			keyValues
		);
		this.emitChanges(dataChange);
	}

	/** "actualizes" the ticket locally */
	public convertPlannedToActual() {
		const dataChange = this.ticketFieldsService.convertPlannedToActual(this.groupedTicketSubject$.value);
		//if it turns out that "actualizing" is necessary, each ticket will need something like
		// newData.actualPhaseOverride = ActualPhase.actualizing;
		this.emitChanges(dataChange);
	}

	// mark ticket as started
	public convertPlannedToStart() {
		const dataChange = this.ticketFieldsService.convertPlannedToStarted(this.groupedTicketSubject$.value);
		this.emitChanges(dataChange);
	}

	/** takes a previously actualized ticket and removes the actual data */
	public convertActualToPlanned() {
		const dataChange = this.ticketFieldsService.revertActualToPlanned(this.groupedTicketSubject$.value);
		this.emitChanges(dataChange);
	}

	/** doctors the changes to output the *id field instead of the whole object (Ticket expects this)  */
	public correctJoinedData(changes: Record<string, unknown>) {
		applyJoinData<Role>(changes, 'liveRole', (l) => ({
			roleId: l.$id,
		}));
		applyJoinData<Location>(changes, 'location', (l) => ({
			locationId: l.$id,
		}));
		applyJoinData<ProjectMember>(changes, 'responsibleProjectMember', (l) => ({
			responsibleProjectMemberId: l.$id,
			responsibleProjectMemberRoldId: l?.view.roleId,
		}));
		applyJoinData<ProjectMember>(changes, 'requestedByProjectMember', (l) => ({
			requestedByProjectMemberId: l.$id,
			requestedByProjectMemberRoleId: l?.view.roleId,
		}));
		applyJoinData<Priority>(changes, 'priority', (priority) => ({
			priorityId: priority.$id,
		}));
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7104
	public updateConstraintAssignment(assignedProjectMember: ProjectMember) {
		const ids = {
			assignedProjectMemberId: assignedProjectMember.$id,
			assignedProjectMemberRoleId: assignedProjectMember.view.roleId,
		};

		const dataChange = this.ticketFieldsService.updateConstraintAssignment(
			this.groupedTicketSubject$.value,
			ids
		);
		this.emitChanges(dataChange);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7104

	public emitChanges(ticketUpdateMap: Map<Ticket, object>) {
		const firstItemInMap = ticketUpdateMap.values().next();
		const dataChanges = firstItemInMap.value;

		//avoid emitting an undefined when the map is empty...
		if (firstItemInMap.done) {
			return;
		}

		this.changedDataSubject$.next(dataChanges);
	}

	public saveChanges() {
		// porting an old hack from the floating ticket <-> edit ticket "temp" bridge
		// there a listener in plan-state that listens for floating ticket close events
		// and when it fires, if that tickets has ticketJustAdded, it'll delete it
		// notably, this hack won't work on mobile, but that probably doesn't matter yet
		this.groupedTicketSubject$.value.srcList.forEach((ticket) => {
			delete ticket.ticketJustAdded;
		});
		this.persistChangesSubject$.next();
		// this.closeForm();
	}

	// public cancelChanges() {
	// 	this.closeForm();
	// }

	public revertData() {
		this.wantsToRevertDataSubject$.next();
	}

	public closeForm() {
		this.wantsToCloseSubject$.next();
	}
}

// helper for correctJoinedData to reduce boilerplate
// mostly just key: undefined checks and deleting the original key
function applyJoinData<T>(
	changes: Record<string, unknown>,
	origKey: string,
	cb: (thing: T) => Record<string, string>
) {
	const origVal = changes[origKey];
	if (origVal) {
		const res = cb(origVal as T);

		Object.entries(res)
			.filter(([key, value]) => value !== undefined)
			.forEach(([key, value]) => {
				changes[key] = value;
			});
		delete changes[origKey];
	}
}
